import React, { useState, useRef, useCallback, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronRight, ChevronLeft, CheckCircle2, XCircle, Loader2, Clock, ArrowLeft, LogIn } from 'lucide-react';
import { validateUsername } from '@/components/lib/usernameValidation';
import { useAuth } from '@/lib/AuthContext';

const slides = [
  {
    title: "Un terrain. Un Duel.",
    text: "Defie un joueur sur un Playground reel. Gagne le Duel, garde le terrain."
  },
  {
    title: "Les Playgrounds sont reels.",
    text: "La communaute ajoute les terrains. Joue la ou les vrais matchs se passent."
  },
  {
    title: "Seules les victoires comptent.",
    text: "Tu gagnes de l'XP uniquement en gagnant. Pas de recompense pour une defaite."
  },
  {
    title: "Choisis ton pseudo",
    text: "Ton identite sur le playground. Choisis-le avec soin, il sera visible par tous.",
    isUsername: true
  },
  {
    title: "Qui t'a invite ?",
    text: "Entre le pseudo de ton parrain pour activer ton compte. L'acces est sur invitation uniquement.",
    isAffiliation: true
  }
];

const LAST_INTRO_SLIDE = 2; // Index of the last intro slide (where login button appears)
const AFFILIATION_SLIDE_INDEX = slides.findIndex(s => s.isAffiliation);
const USERNAME_SLIDE_INDEX = slides.findIndex(s => s.isUsername);

/**
 * Onboarding - Flux d'onboarding verrouille
 *
 * Navigation:
 * - Slides 0, 1: Suivant/Precedent
 * - Slide 2: S'inscrire (login)
 * - Apres login: Pseudo -> Affiliation
 */
export default function Onboarding() {
  const { user, isAuthenticated, navigateToLogin } = useAuth();

  // Determiner le slide initial
  const getInitialSlide = () => {
    // Si authentifie, aller directement au pseudo ou affiliation
    if (isAuthenticated) {
      if (user?.username && user?.access_status !== 'ACTIVE') return AFFILIATION_SLIDE_INDEX;
      if (user?.username) return AFFILIATION_SLIDE_INDEX;
      return USERNAME_SLIDE_INDEX; // Aller au pseudo si authentifie
    }
    return 0;
  };

  const [currentSlide, setCurrentSlide] = useState(getInitialSlide);
  const [isLoading, setIsLoading] = useState(false);
  const [username, setUsername] = useState(user?.username || '');
  const [usernameStatus, setUsernameStatus] = useState(user?.username ? 'available' : null);
  const [usernameError, setUsernameError] = useState('');

  // Affiliation state
  const [referrerUsername, setReferrerUsername] = useState('');
  const [referrerStatus, setReferrerStatus] = useState(null);
  const [referrerError, setReferrerError] = useState('');
  const [referrerSlotsRemaining, setReferrerSlotsRemaining] = useState(null);
  const [affiliationResult, setAffiliationResult] = useState(null);
  const referrerCheckTimeout = useRef(null);

  // Bloquer le bouton back OS pendant l'onboarding
  useEffect(() => {
    const handlePopState = (e) => {
      e.preventDefault();

      if (currentSlide > 0) {
        // Si authentifie et sur username, ne pas reculer avant username
        if (isAuthenticated && currentSlide === USERNAME_SLIDE_INDEX) {
          // Rester sur username
        } else if (currentSlide === AFFILIATION_SLIDE_INDEX && user?.username) {
          // Rester sur affiliation si on a deja un username
        } else {
          setCurrentSlide(prev => Math.max(0, prev - 1));
        }
      }

      // Re-push state pour empecher la sortie de l'onboarding
      window.history.pushState({ onboarding: true }, '');
    };

    // Push initial pour capturer le back
    window.history.pushState({ onboarding: true }, '');
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentSlide, user?.username, isAuthenticated]);

  const checkUsername = async (value) => {
    const validation = validateUsername(value);
    if (!validation.valid) {
      setUsernameStatus('error');
      setUsernameError(validation.error);
      return;
    }

    setUsernameStatus('checking');
    setUsernameError('');

    try {
      const { data } = await supabase.from('users').select('id').eq('username', value);
      const available = !data || data.length === 0;
      if (available) {
        setUsernameStatus('available');
        setUsernameError('');
      } else {
        setUsernameStatus('taken');
        setUsernameError('Ce pseudo est deja pris');
      }
    } catch {
      setUsernameStatus('error');
      setUsernameError('Erreur de verification');
    }
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    if (value.trim().length >= 1) {
      clearTimeout(window.usernameCheckTimeout);
      window.usernameCheckTimeout = setTimeout(() => checkUsername(value), 500);
    } else {
      setUsernameStatus(null);
      setUsernameError('');
    }
  };

  const checkReferrer = useCallback(async (value) => {
    const trimmed = value.trim();
    if (!trimmed || trimmed.length < 4) {
      setReferrerStatus(null);
      setReferrerError('');
      setReferrerSlotsRemaining(null);
      return;
    }

    setReferrerStatus('checking');
    setReferrerError('');

    try {
      // Check if it's a valid username
      const { data: userMatch } = await supabase.from('users').select('id').ilike('username', trimmed);
      if (userMatch && userMatch.length > 0) {
        setReferrerStatus('valid');
        setReferrerSlotsRemaining(null);
        setReferrerError('');
        return;
      }

      // Check if it's a valid affiliation code
      const { data: codeMatch } = await supabase.from('affiliation_codes').select('id').eq('code', trimmed).eq('is_used', false);
      if (codeMatch && codeMatch.length > 0) {
        setReferrerStatus('valid');
        setReferrerSlotsRemaining(null);
        setReferrerError('');
        return;
      }

      setReferrerStatus('invalid');
      setReferrerError('Parrain ou code introuvable');
      setReferrerSlotsRemaining(null);
    } catch {
      setReferrerStatus('invalid');
      setReferrerError('Erreur de verification');
    }
  }, []);

  const handleReferrerChange = (e) => {
    const value = e.target.value;
    setReferrerUsername(value);
    setAffiliationResult(null);

    if (referrerCheckTimeout.current) clearTimeout(referrerCheckTimeout.current);

    if (value.trim().length >= 4) {
      referrerCheckTimeout.current = setTimeout(() => checkReferrer(value), 500);
    } else {
      setReferrerStatus(null);
      setReferrerError('');
      setReferrerSlotsRemaining(null);
    }
  };

  const handleNext = async () => {
    const slide = slides[currentSlide];

    if (slide.isUsername) {
      if (usernameStatus !== 'available') {
        setUsernameError('Veuillez choisir un pseudo disponible');
        return;
      }
      setIsLoading(true);
      try {
        await supabase.from('users').update({
          username: username.toLowerCase()
        }).eq('id', user.id);
        setCurrentSlide(currentSlide + 1);
      } catch {
        setUsernameError('Erreur lors de la sauvegarde');
      } finally {
        setIsLoading(false);
      }
    } else if (slide.isAffiliation) {
      await handleAffiliation();
    } else if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleAffiliation = async () => {
    if (referrerStatus !== 'valid' && referrerStatus !== 'full') {
      setReferrerError('Entre le pseudo d\'un parrain valide pour continuer');
      return;
    }

    setIsLoading(true);
    setReferrerError('');

    try {
      const referrerInput = referrerUsername.trim();
      const { data: { user: authUser } } = await supabase.auth.getUser();

      if (!authUser) {
        setReferrerError('Tu dois être connecté pour activer ton compte');
        setIsLoading(false);
        return;
      }

      // Find referrer by username
      const { data: referrerData } = await supabase.from('users').select('id').ilike('username', referrerInput).maybeSingle();

      // Create affiliation record
      await supabase.from('affiliations').insert({
        affiliate_user_id: authUser.id,
        referrer_user_id: referrerData?.id,
        referrer_username: referrerInput
      });

      // Activate user
      await supabase.from('users').update({ access_status: 'ACTIVE' }).eq('id', authUser.id);

      setAffiliationResult('ACTIVE');
      setTimeout(async () => {
        await completeOnboarding();
      }, 2000);
    } catch (error) {
      const errorMsg = error.message || 'Erreur lors de l\'affiliation';
      setReferrerError(errorMsg);
      setIsLoading(false);
    }
  };

  const completeOnboarding = async () => {
    setIsLoading(true);
    try {
      await supabase.from('users').upsert({
        id: user.id,
        email: user.email,
        username: username.toLowerCase(),
        onboarding_completed: true,
        access_status: 'ACTIVE'
      });
      window.location.href = '/';
    } catch {
      setUsernameError('Erreur lors de la sauvegarde');
      setIsLoading(false);
    }
  };

  const handleLogin = () => {
    navigateToLogin();
  };

  const slide = slides[currentSlide];
  const isIntroSlide = currentSlide <= LAST_INTRO_SLIDE;
  const isLastIntroSlide = currentSlide === LAST_INTRO_SLIDE;
  const canSubmitAffiliation = referrerStatus === 'valid' || referrerStatus === 'full';

  // Determiner si on peut aller en arriere
  const canGoBack = currentSlide > 0 &&
    !(isAuthenticated && currentSlide === USERNAME_SLIDE_INDEX) &&
    !(currentSlide === AFFILIATION_SLIDE_INDEX && user?.username);

  return (
    <div className="bg-gradient-to-br from-[#0A0B10] to-[#13151D] min-h-screen flex flex-col items-center justify-center p-6 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#6E2BFF] rounded-full blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#8B5CFF] rounded-full blur-3xl opacity-10 animate-pulse"></div>
      </div>

      {/* Back button (top left) - only for username/affiliation slides when allowed */}
      {canGoBack && !isIntroSlide && !affiliationResult && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handlePrevious}
          className="absolute top-6 left-6 text-white hover:text-[#6E2BFF] z-20"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
      )}

      <div className="relative z-10 max-w-md w-full">
        <div className="text-center space-y-8 mb-16">
          <div className="space-y-4">
            <h1 className="text-5xl font-black tracking-tight text-white">
              {slide.title}
            </h1>
            <p className="text-xl text-[#B3B6C2] leading-relaxed">
              {slide.text}
            </p>
          </div>

          {/* Username Input */}
          {slide.isUsername && (
            <div className="space-y-3 mt-8">
              <div className="relative">
                <Input
                  type="text"
                  placeholder="ton_pseudo"
                  value={username}
                  onChange={handleUsernameChange}
                  className="bg-[#1B1E28] border-[#6E2BFF] text-white text-center text-lg py-6 pr-12"
                  maxLength={20}
                  autoFocus
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  {usernameStatus === 'checking' && <Loader2 className="w-5 h-5 text-[#B3B6C2] animate-spin" />}
                  {usernameStatus === 'available' && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                  {(usernameStatus === 'taken' || usernameStatus === 'error') && <XCircle className="w-5 h-5 text-red-500" />}
                </div>
              </div>
              {usernameError && <p className="text-sm text-red-400">{usernameError}</p>}
              {usernameStatus === 'available' && <p className="text-sm text-green-400">Ce pseudo est disponible</p>}
              <p className="text-xs text-[#B3B6C2]">
                4-20 caracteres - Lettres, chiffres et _ - Pas de _ au debut/fin
              </p>
            </div>
          )}

          {/* Affiliation Input */}
          {slide.isAffiliation && (
            <div className="space-y-3 mt-8">
              {affiliationResult === 'ACTIVE' ? (
                <div className="text-center space-y-4">
                  <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
                  <p className="text-lg text-green-400 font-bold">Compte active !</p>
                  <p className="text-sm text-[#B3B6C2]">+10 XP pour toi et ton parrain</p>
                  <p className="text-xs text-[#B3B6C2]">Redirection...</p>
                </div>
              ) : affiliationResult === 'WAITLIST' ? (
                <div className="text-center space-y-4">
                  <Clock className="w-16 h-16 text-yellow-500 mx-auto" />
                  <p className="text-lg text-yellow-400 font-bold">Liste d'attente</p>
                  <p className="text-sm text-[#B3B6C2]">
                    Ton parrain a atteint sa limite d'invitations.
                    Tu seras active des qu'une place se libere.
                  </p>
                  <p className="text-xs text-[#B3B6C2] mt-4">
                    Tu peux aussi essayer un autre parrain :
                  </p>
                  <Button
                    onClick={() => {
                      setAffiliationResult(null);
                      setReferrerUsername('');
                      setReferrerStatus(null);
                      setReferrerError('');
                      setIsLoading(false);
                    }}
                    className="bg-[#1B1E28] hover:bg-[#252836] text-white border border-[#6E2BFF] text-sm py-2 px-4"
                  >
                    Changer de parrain
                  </Button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="pseudo ou code d'invitation"
                      value={referrerUsername}
                      onChange={handleReferrerChange}
                      className="bg-[#1B1E28] border-[#6E2BFF] text-white text-center text-lg py-6 pr-12"
                      maxLength={20}
                      autoCapitalize="none"
                      autoFocus
                      disabled={isLoading}
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      {referrerStatus === 'checking' && <Loader2 className="w-5 h-5 text-[#B3B6C2] animate-spin" />}
                      {referrerStatus === 'valid' && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                      {referrerStatus === 'full' && <Clock className="w-5 h-5 text-yellow-500" />}
                      {referrerStatus === 'invalid' && <XCircle className="w-5 h-5 text-red-500" />}
                    </div>
                  </div>

                  {referrerError && <p className="text-sm text-red-400">{referrerError}</p>}

                  {referrerStatus === 'valid' && (
                    <p className="text-sm text-green-400">
                      {referrerSlotsRemaining != null
                        ? `Parrain trouve - ${referrerSlotsRemaining} place${referrerSlotsRemaining > 1 ? 's' : ''} restante${referrerSlotsRemaining > 1 ? 's' : ''}`
                        : 'Code valide'}
                    </p>
                  )}

                  {referrerStatus === 'full' && (
                    <p className="text-sm text-yellow-400">
                      Ce parrain a atteint sa limite. Tu seras en liste d'attente.
                    </p>
                  )}

                  <p className="text-xs text-[#B3B6C2]">
                    Entre le pseudo d'un parrain ou un code d'invitation
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Progress Dots */}
        <div className="flex justify-center gap-2 mb-16">
          {slides.map((_, index) => (
            <div
              key={index}
              className={`h-2 rounded-full transition-all ${
                index === currentSlide ? 'w-8 bg-[#6E2BFF]' : 'w-2 bg-[#1B1E28]'
              }`}
            ></div>
          ))}
        </div>

        {/* Buttons */}
        {!affiliationResult && (
          <>
            {/* Intro slides navigation (slides 0, 1, 2) */}
            {isIntroSlide && !isAuthenticated && (
              <div className="space-y-3">
                {/* Last intro slide: show login button */}
                {isLastIntroSlide ? (
                  <Button
                    onClick={handleLogin}
                    className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2 text-lg"
                  >
                    <LogIn className="w-5 h-5" />
                    S'inscrire
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2 text-lg"
                  >
                    Suivant
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                )}

                {/* First intro slide: show "Se connecter" link under Next */}
                {currentSlide === 0 && (
                  <Button
                    variant="ghost"
                    onClick={handleLogin}
                    className="w-full text-[#B3B6C2] hover:text-white transition-all flex items-center justify-center gap-2"
                  >
                    Se connecter
                  </Button>
                )}

                {/* Previous button for slides 1 and 2 */}
                {currentSlide > 0 && (
                  <Button
                    variant="ghost"
                    onClick={handlePrevious}
                    className="w-full text-[#B3B6C2] hover:text-white transition-all flex items-center justify-center gap-2"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    Precedent
                  </Button>
                )}
              </div>
            )}

            {/* Username slide */}
            {slide.isUsername && (
              <Button
                onClick={handleNext}
                disabled={isLoading || usernameStatus !== 'available'}
                className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading && <Loader2 className="w-5 h-5 animate-spin" />}
                {!isLoading && (
                  <>
                    Suivant
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </Button>
            )}

            {/* Affiliation slide */}
            {slide.isAffiliation && (
              <Button
                onClick={handleNext}
                disabled={isLoading || !canSubmitAffiliation}
                className="w-full bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading && <Loader2 className="w-5 h-5 animate-spin" />}
                {!isLoading && (
                  referrerStatus === 'full' ? 'Rejoindre la liste d\'attente' : 'Activer mon compte'
                )}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
